import React from 'react';

import BodyRegular from 'components/Typography/BodyRegular';

import { COMPLETED, EXTRA_STATE, MEDIUM_BLUE } from 'constants/colors';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import { formatarDataHoraCurta, labelDaVariante } from 'helper/Relatorios/Aprovacao';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import {
  datasDoInforme, formatarDataCurtaComSemana, formatarDataPorExtenso, fraseDoInforme,
} from 'helper/Relatorios/Dessem';
import { DessemCenario, DessemSecaoParams } from 'types/Relatorios/Dessem';
import { useSeriesColor } from 'hooks/theme/useChartTheme';

import './index.scss';

const config = getAprovacaoConfig('dessem');

/**
 * Cor de cada cenário (ponto e filete da faixa): azul, verde e laranja que já existem no
 * design system (`constants/colors`). A cor é só reforço visual — o texto nomeia o cenário,
 * então nada depende dela (e o texto fica em cinza-escuro, legível, não na cor).
 */
// tema: dados-inicio — cor de cenário (adaptada por useSeriesColor no escuro)
export const CENARIO_COR: Record<DessemCenario, string> = {
  early: MEDIUM_BLUE,
  preliminar: COMPLETED,
  pos_ons: EXTRA_STATE,
};
// tema: dados-fim

export interface ContextoDessemProps {
  params: DessemSecaoParams;
}

/**
 * Faixa de contexto no topo do conteúdo de uma seção do Informe DESSEM. Resume a frase das
 * "Considerações" do relatório do Planejamento ("deck DESSEM para o dia D+1 com base no deck
 * oficial CCEE do dia D") numa mini linha do tempo:
 *
 *   ● Cenário Early   [D] seg 12/10/2026 deck oficial CCEE → [D+1] ter 13/10/2026 previsão DESSEM
 *                                                              processado em 12/10/2026 20:58
 *
 * O D+1 (o dia que os gráficos mostram) leva o selo com borda na cor do cenário (texto escuro,
 * para manter o contraste também no verde e no laranja) e a data em negrito; o D fica discreto.
 * A linha do tempo é `aria-hidden`: o leitor de tela lê a frase completa (`visually-hidden`),
 * que também aparece no `title` da faixa. "processado em dd/MM/yyyy HH:mm"
 * (à direita) só quando o status de aprovação traz `estudo_processado_em`. Data inválida mostra
 * a data como veio, sem linha do tempo.
 * Visível para admin e cliente mesmo com o painel de controle recolhido.
 *
 * Recebe os `params` da consulta da seção (`useDessemSecaoParams`): é sempre o cenário APLICADO,
 * o mesmo das consultas (desde 13/10/2026 o painel aplica no clique, sem rascunho nem botão).
 * Quem decide quando mostrar a faixa é o `SecaoDessemEstado`.
 *
 * O status vem de `useRelatorioAprovacao` com a config explícita (não o contexto do
 * `RelatorioSecoesView`, que nem sempre envolve a faixa): a consulta é a MESMA do
 * `PublicacaoGate`/`RevisaoBar` para o mesmo dia/cenário (o slice é a fonte dos dois), então o
 * RTK Query compartilha o cache e nenhuma requisição nova sai daqui. Com a flag de aprovação
 * desligada a consulta é `skip`ada e a faixa fica sem o "processado em".
 */
export default function ContextoDessem({ params }: ContextoDessemProps) {
  const { data_rodada: dataRodada, cenario } = params;
  const corSerie = useSeriesColor();
  const cor = corSerie(CENARIO_COR[cenario]);
  const { status } = useRelatorioAprovacao(config);
  const processadoEm = status?.estudo_processado_em ?? null;
  const datas = datasDoInforme(dataRodada);
  const rotuloCenario = `Cenário ${labelDaVariante(config, cenario)}`;

  const frase = datas === null ? null : fraseDoInforme(datas);

  return (
    <div
      className={`dessem-contexto dessem-contexto--${cenario}`}
      style={{ borderLeftColor: cor }}
      title={frase ?? undefined}
    >
      <BodyRegular>
        <span className="dessem-contexto__cenario">
          <span
            className="dessem-contexto__dot"
            style={{ backgroundColor: cor }}
            aria-hidden="true"
          />
          <span className="dessem-contexto__rotulo">{rotuloCenario}</span>
        </span>

        {datas === null ? (
          <span className="dessem-contexto__data">{formatarDataPorExtenso(dataRodada)}</span>
        ) : (
          <>
            <span className="dessem-contexto__linha-do-tempo" aria-hidden="true">
              <span className="dessem-contexto__marco dessem-contexto__marco--base">
                <span className="dessem-contexto__selo">D</span>
                <span className="dessem-contexto__data">{formatarDataCurtaComSemana(datas.d)}</span>
                <span className="dessem-contexto__legenda">deck oficial CCEE</span>
              </span>
              <span className="dessem-contexto__seta">→</span>
              <span className="dessem-contexto__marco dessem-contexto__marco--previsao">
                <span className="dessem-contexto__selo" style={{ borderColor: cor }}>D+1</span>
                <span className="dessem-contexto__data">{formatarDataCurtaComSemana(datas.dMais1)}</span>
                <span className="dessem-contexto__legenda">previsão DESSEM</span>
              </span>
            </span>
            <span className="visually-hidden">{frase}</span>
          </>
        )}

        {processadoEm !== null && (
          <span className="dessem-contexto__processado">
            {`processado em ${formatarDataHoraCurta(processadoEm)}`}
          </span>
        )}
      </BodyRegular>
    </div>
  );
}
